import { Vector3 } from '@babylonjs/core';
import { Level } from '../Level';
import { ObstacleDistributionConfig } from '../ObstacleGenerator';
import { ObstacleBehavior } from './ObstacleBehavior';

/**
 * Magnet Behavior – alternates between attracting and repelling the player.
 *
 * Config fields:
 *   magnetRadius  – effective radius (default 12)
 *   magnetForce   – force strength applied per frame (default 8)
 *   fireInterval  – seconds between polarity flips (default 3)
 *   speed         – visual pulse speed (default 1)
 */
export class ObstacleMagnetBehavior extends ObstacleBehavior {
  private time: number = 0;
  private attracting: boolean = true; // start attracting
  private pulseTime: number = 0;
  private baseScale: Vector3 = Vector3.One();

  constructor(startPosition: Vector3, pathIndex: number, config: ObstacleDistributionConfig) {
    super('obstacleMagnet', startPosition, pathIndex, config);
  }

  attach(mesh: import('@babylonjs/core').AbstractMesh): void {
    super.attach(mesh);
    this.baseScale = mesh.scaling.clone();
  }

  initLevel(level: Level): ObstacleMagnetBehavior {
    super.initLevel(level);

    // Register spatial sound so play('checkpoint', this.mesh) can find it
    this.level.game.sound.spatial('checkpoint', this.mesh);

    return this;
  }

  update(deltaTime: number): void {
    if (!this.level?.player) return;

    this.time += deltaTime;
    this.pulseTime += deltaTime * (this.config.speed ?? 1);

    const flipInterval = this.config.fireInterval ?? 3;

    // Polarity flip
    const prevAttracting = this.attracting;
    this.attracting = Math.floor(this.time / flipInterval) % 2 === 0;
    if (prevAttracting !== this.attracting) {
      // Play a click sound on flip
      this.level.game.sound.play('checkpoint', this.mesh);
    }

    const radius = this.config.magnetRadius ?? 12;
    const force = this.config.magnetForce ?? 18;

    const playerPos = this.level.player.getPosition();
    const toPlayer = playerPos.subtract(this.mesh.position);
    const dist = toPlayer.length();

    if (dist < radius && dist > 0.5) {
      const normalized = toPlayer.normalize();
      const falloff = 1 - dist / radius; // stronger near center
      const direction = this.attracting ? normalized.negate() : normalized;
      const impulse = direction.scale(force * falloff * deltaTime);
      this.level.player.applyExternalImpulse(impulse);
    }

    // Visual pulse
    const pulseFactor = this.attracting
      ? 1 + 0.12 * Math.abs(Math.sin(this.pulseTime * 3))
      : 1 - 0.08 * Math.abs(Math.sin(this.pulseTime * 3));

    this.mesh.scaling.copyFrom(this.baseScale.scale(pulseFactor));
  }

  clone(): ObstacleMagnetBehavior {
    return new ObstacleMagnetBehavior(this.startPosition, this.pathIndex, this.config);
  }
}
